/**
 * The import sources, in one place.
 *
 * The list used to live inline in `App.vue`, reachable only from a TieredMenu
 * nested inside the avatar menu — so a Pocket/Raindrop/Instapaper migrant who
 * skipped the tour had no way to find it from the empty library screen. The
 * empty state now offers the same menu, and forking the list to do that is how
 * a newly added source ends up missing from one of the two copies. Every
 * surface that offers "import" builds its menu from `IMPORT_SOURCES`.
 *
 * Ids are the same slugs `App.vue` maps to import dialogs (and the tour's
 * `open-import` payload uses a subset of them), so they double as the analytics
 * `mode` value — a small fixed enum, never user data.
 */
import { computed } from 'vue'
import { useI18n } from 'petite-vue-i18n'

export const IMPORT_SOURCES = [
  { id: 'url-list', labelKey: 'app.importMenu.urlList', icon: 'pi pi-list' },
  { id: 'raindrop', labelKey: 'app.importMenu.raindrop', icon: 'pi pi-file-import' },
  { id: 'chrome', labelKey: 'app.importMenu.chrome', icon: 'pi pi-google' },
  { id: 'pocket', labelKey: 'app.importMenu.pocket', icon: 'pi pi-bookmark' },
  { id: 'instapaper', labelKey: 'app.importMenu.instapaper', icon: 'pi pi-file' },
  { id: 'goodreads', labelKey: 'app.importMenu.goodreads', icon: 'pi pi-book' },
  { id: 'youtube', labelKey: 'app.importMenu.youtube', icon: 'pi pi-youtube' },
  { id: 'karakeep', labelKey: 'app.importMenu.karakeep', icon: 'pi pi-box' },
  { id: 'linkwarden', labelKey: 'app.importMenu.linkwarden', icon: 'pi pi-server' },
]

/** Window event `App.vue` listens for; it owns every import dialog. */
export const OPEN_IMPORT_MODAL_EVENT = 'open-import-modal'

/**
 * PrimeVue menu model for the import sources. Computed (not a static array) so
 * a live language switch re-labels the menu.
 *
 * @param {(id: string) => void} onSelect runs with the chosen source id
 */
export function useImportMenu(onSelect) {
  const { t } = useI18n()
  return computed(() =>
    IMPORT_SOURCES.map(source => ({
      label: t(source.labelKey),
      icon: source.icon,
      command: () => onSelect(source.id),
    }))
  )
}

/**
 * Asks the app shell to open an import dialog. Views outside `App.vue` cannot
 * reach the dialog state directly; this mirrors the existing
 * `open-subscription-modal` bridge.
 */
export function requestImport(id) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(OPEN_IMPORT_MODAL_EVENT, { detail: { type: id } }))
}
